import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format, subDays } from 'date-fns';
import superagent from 'superagent';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { backendUrl } from '@helpers/consts';
import { Button } from '@shadcn/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@shadcn/components/ui/card';
import { Badge } from '@shadcn/components/ui/badge';
import { TablePagination } from '@components/supply-chain/TablePagination';
import { Download, ExternalLink, PackageCheck, PackageX, Truck } from 'lucide-react';

const DEFAULT_SUMMARY = {
  totalShipped: 0,
  fulfilledInJifi: 0,
  fulfilledExternally: 0,
  withTracking: 0,
  withoutTracking: 0,
};

const getDefaultStartDate = () => format(subDays(new Date(), 7), 'yyyy-MM-dd');
const getDefaultEndDate = () => format(new Date(), 'yyyy-MM-dd');

const formatDateTime = (value) => {
  if (!value) return '-';
  return format(new Date(value), 'dd MMM yyyy, h:mm a');
};

const formatExportDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatMoney = (value) => {
  if (value === undefined || value === null || value === '') return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number(value));
};

const formatBoolean = (value) => (value ? 'True' : 'False');

const buildQuery = (params) => Object.entries(params).reduce((query, [key, value]) => {
  if (value !== undefined && value !== null && value !== '') {
    query[key] = value;
  }
  return query;
}, {});

const escapeCsvValue = (value) => {
  const stringValue = value === undefined || value === null ? '' : String(value);
  return `"${stringValue.replace(/"/g, '""')}"`;
};

const downloadCsv = (rows) => {
  if (!rows.length) {
    toast.warn('No shipped orders found for the selected filters.');
    return;
  }

  const headers = [
    'Order',
    'Order Created',
    'Fulfillment At',
    'Fulfilled By',
    'Ready To Ship',
    'Order Source',
    'Integration Source',
    'Shipping Type',
    'Total Price',
    'SKUs',
    'Line Items',
    'Quantity',
    'Customer',
    'State',
    'Country',
    'Tracking Company',
    'Tracking Number',
    'Tracking URL',
  ];

  const csvRows = rows.map((item) => [
    item.name,
    formatExportDateTime(item.orderCreated),
    formatExportDateTime(item.shippedAt),
    item.fulfillmentSource,
    formatBoolean(item.readyToShip),
    item.orderSource,
    item.integrationSource,
    item.shippingType,
    item.totalPrice,
    item.skus,
    item.lineItemCount,
    item.itemQuantity,
    item.customerName,
    item.state,
    item.country,
    item.trackingCompany,
    item.trackingNumber,
    item.trackingUrl,
  ].map(escapeCsvValue).join(','));

  const blob = new Blob([`\uFEFF${[headers.map(escapeCsvValue).join(','), ...csvRows].join('\n')}`], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `shipped-orders-${format(new Date(), 'yyyy-MM-dd')}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const ShippedOrdersReport = () => {
  const [filterStartDate, setFilterStartDate] = useState(getDefaultStartDate());
  const [filterEndDate, setFilterEndDate] = useState(getDefaultEndDate());
  const [fulfillmentSource, setFulfillmentSource] = useState('all');
  const [readyToShip, setReadyToShip] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({
    filterStartDate: getDefaultStartDate(),
    filterEndDate: getDefaultEndDate(),
    fulfillmentSource: 'all',
    readyToShip: 'all',
    searchTerm: '',
  });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [orders, setOrders] = useState([]);
  const [count, setCount] = useState(0);
  const [summary, setSummary] = useState(DEFAULT_SUMMARY);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const queryParams = useMemo(() => buildQuery({
    page,
    perPage,
    filterStartDate: appliedFilters.filterStartDate,
    filterEndDate: appliedFilters.filterEndDate,
    fulfillmentSource: appliedFilters.fulfillmentSource,
    readyToShip: appliedFilters.readyToShip,
    searchTerm: appliedFilters.searchTerm,
  }), [appliedFilters, page, perPage]);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const response = await superagent
        .get(`${backendUrl}/orders/shipped-report`)
        .query(queryParams)
        .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`)
        .set('accept', 'json');

      setOrders(response.body?.data || []);
      setCount(response.body?.count || 0);
      setSummary(response.body?.summary || DEFAULT_SUMMARY);
    } catch (error) {
      console.error('Error fetching shipped orders report:', error);
      toast.error('Unable to fetch shipped orders report. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const applyFilters = () => {
    setPage(1);
    setAppliedFilters({
      filterStartDate,
      filterEndDate,
      fulfillmentSource,
      readyToShip,
      searchTerm: searchTerm.trim(),
    });
  };

  const clearFilters = () => {
    const defaultStartDate = getDefaultStartDate();
    const defaultEndDate = getDefaultEndDate();
    setFilterStartDate(defaultStartDate);
    setFilterEndDate(defaultEndDate);
    setFulfillmentSource('all');
    setReadyToShip('all');
    setSearchTerm('');
    setPage(1);
    setAppliedFilters({
      filterStartDate: defaultStartDate,
      filterEndDate: defaultEndDate,
      fulfillmentSource: 'all',
      readyToShip: 'all',
      searchTerm: '',
    });
  };

  const exportReport = async () => {
    try {
      setExporting(true);
      const response = await superagent
        .get(`${backendUrl}/orders/shipped-report`)
        .query({
          ...queryParams,
          page: 1,
          perPage: count || 10000,
        })
        .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`)
        .set('accept', 'json');

      downloadCsv(response.body?.data || []);
    } catch (error) {
      console.error('Error exporting shipped orders report:', error);
      toast.error('Unable to export shipped orders report. Please try again later.');
    } finally {
      setExporting(false);
    }
  };

  const summaryCards = [
    { label: 'Total Shipped', value: summary.totalShipped, icon: Truck, color: 'bg-primary' },
    { label: 'Fulfilled in JIFI', value: summary.fulfilledInJifi, icon: PackageCheck, color: 'bg-success' },
    { label: 'External Fulfilled', value: summary.fulfilledExternally, icon: PackageCheck, color: 'bg-info' },
    { label: 'With Tracking', value: summary.withTracking, icon: ExternalLink, color: 'bg-primary' },
    { label: 'No Tracking', value: summary.withoutTracking, icon: PackageX, color: 'bg-warning' },
  ];

  return (
    <div className="px-2 sm:px-4">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Shipped Orders Report</h2>
        <CardDescription className="mt-2">
          Orders recorded as shipped or fulfilled, including orders fulfilled before entering JIFI and orders fulfilled inside JIFI.
        </CardDescription>
      </div>

      <Card className="mb-4">
        <CardContent className="pt-6">
          <div className="flex flex-col lg:flex-row lg:items-end gap-3">
            <div className="w-full lg:flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Shipped From</label>
              <input
                type="date"
                className="form-control form-control-sm w-full"
                value={filterStartDate}
                onChange={(event) => setFilterStartDate(event.target.value)}
              />
            </div>
            <div className="w-full lg:flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Shipped To</label>
              <input
                type="date"
                className="form-control form-control-sm w-full"
                value={filterEndDate}
                onChange={(event) => setFilterEndDate(event.target.value)}
              />
            </div>
            <div className="w-full lg:w-44">
              <label className="block text-sm font-medium text-gray-700 mb-1">Fulfilled By</label>
              <select
                className="form-control form-control-sm w-full"
                value={fulfillmentSource}
                onChange={(event) => setFulfillmentSource(event.target.value)}
              >
                <option value="all">All</option>
                <option value="JIFI">JIFI</option>
                <option value="External">External</option>
              </select>
            </div>
            <div className="w-full lg:w-44">
              <label className="block text-sm font-medium text-gray-700 mb-1">Ready To Ship</label>
              <select
                className="form-control form-control-sm w-full"
                value={readyToShip}
                onChange={(event) => setReadyToShip(event.target.value)}
              >
                <option value="all">All</option>
                <option value="true">True</option>
                <option value="false">False</option>
              </select>
            </div>
            <div className="w-full lg:flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <input
                className="form-control form-control-sm w-full"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Order, SKU, customer"
                onKeyDown={(event) => {
                  if (event.key === 'Enter') applyFilters();
                }}
              />
            </div>
            <div className="flex items-end gap-2 self-end">
              <button
                type="button"
                disabled={loading}
                onClick={applyFilters}
                className="btn btn-primary btn-sm inline-flex items-center justify-center whitespace-nowrap px-3"
                style={{ height: 31, lineHeight: 1, paddingTop: 0, paddingBottom: 0 }}
              >
                {loading ? 'Loading...' : 'Apply Filter'}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm inline-flex items-center justify-center whitespace-nowrap px-3"
                style={{ height: 31, lineHeight: 1, paddingTop: 0, paddingBottom: 0 }}
                onClick={clearFilters}
              >
                Clear
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
        {summaryCards.map(({ label, value, icon: Icon, color }) => (
          <Card key={label} className="mini-stats-wid bg-light h-full">
            <CardContent className="h-full pt-6">
              <div className="flex h-full">
                <div className="flex-grow-1">
                  <h4 className="mb-0 text-xl font-semibold">{Number(value || 0).toLocaleString()}</h4>
                  <p className="mb-0 mt-1 text-sm font-semibold text-gray-800">{label}</p>
                </div>
                <div className={`avatar-sm rounded-circle ${color} align-self-center mini-stat-icon`}>
                  <span className={`avatar-title rounded-circle ${color}`}>
                    <Icon className="h-5 w-5 text-white" />
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>
              Shipped Orders
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                {count.toLocaleString()} order(s) match the current filters.
              </span>
            </CardTitle>
          </div>
          <Button variant="outline" disabled={exporting || loading} onClick={exportReport}>
            <Download className="h-4 w-4 mr-2" />
            {exporting ? 'Exporting...' : 'Export CSV'}
          </Button>
        </CardHeader>
        <CardContent>
          <div className="table-responsive">
            <table className="table table-centered table-nowrap">
              <thead className="thead-light">
                <tr>
                  <th>Order</th>
                  <th>Fulfillment At</th>
                  <th className="text-center">Fulfilled By</th>
                  <th>Ready To Ship</th>
                  <th>Order Source</th>
                  <th>Shipping</th>
                  <th>Customer</th>
                  <th>Country</th>
                  <th>Qty</th>
                  <th>Total</th>
                  <th>Tracking</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="11" className="text-center p-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Loading...</span>
                      </div>
                      <p className="mt-2">Loading shipped orders...</p>
                    </td>
                  </tr>
                ) : orders.length ? (
                  orders.map((order) => (
                    <tr key={order.id}>
                      <td>
                        <Link to={`/orders/${order.id}`} className="font-medium text-primary">
                          {order.name}
                        </Link>
                      </td>
                      <td>{formatDateTime(order.shippedAt)}</td>
                      <td className="text-center">
                        <Badge
                          variant={order.fulfillmentSource === 'JIFI' ? 'default' : 'outline'}
                          style={{ minWidth: 64, justifyContent: 'center' }}
                          className={
                            order.fulfillmentSource === 'External'
                              ? 'border-blue-100 bg-blue-100 text-blue-800'
                              : ''
                          }
                        >
                          {order.fulfillmentSource}
                        </Badge>
                      </td>
                      <td>
                        <Badge
                          variant={order.readyToShip ? 'default' : 'outline'}
                          style={{ minWidth: 56, justifyContent: 'center' }}
                          className={
                            order.readyToShip
                              ? 'bg-green-100 text-green-800 hover:bg-green-100'
                              : 'border-red-100 bg-red-100 text-red-800'
                          }
                        >
                          {formatBoolean(order.readyToShip)}
                        </Badge>
                      </td>
                      <td>{order.orderSource || '-'}</td>
                      <td>{order.shippingType || '-'}</td>
                      <td>{order.customerName || '-'}</td>
                      <td>{[order.state, order.country].filter(Boolean).join(', ') || '-'}</td>
                      <td>{order.itemQuantity || 0}</td>
                      <td>{formatMoney(order.totalPrice)}</td>
                      <td>
                        {order.trackingUrl ? (
                          <a href={order.trackingUrl} target="_blank" rel="noreferrer" className="text-primary">
                            Open <ExternalLink className="inline h-3 w-3" />
                          </a>
                        ) : '-'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="11" className="text-center p-5">
                      No shipped orders found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {count > 0 && (
            <TablePagination
              count={count}
              page={page}
              setPage={setPage}
              perPage={perPage}
              setPerPage={(value) => {
                setPage(1);
                setPerPage(value);
              }}
              className="mt-4"
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ShippedOrdersReport;
